import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';

import { observer } from 'mobx-react-lite';
import { action, runInAction } from 'mobx';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';

import { Blueprint, Policy } from '@vromo/models';
import { Site } from '@vromo/models';

import Input, { InputLabel } from '@vromo/ui/components/Input';
import Select from '@vromo/ui/components/Select2';
import TelInput from '@vromo/dashboard/src/common/components/ControlledTelInput/ControlledTelInput';

import sortBy from 'lodash/sortBy';
import AutodispatcherHeader from './AutodispatcherHeader';
import {qaLabelTestSites} from './../../../../../../common-qa-labels/sites'

const FilterRow = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px 0px;

    & > label {
        flex: 1;
    }

    input,
    select {
        margin-top: 5px;
    }
`;

type NumberOption = {
    label: string;
    value: number;
};

type Props = {
    editedSite: Site;
    policyOptions: { label: string; value: number }[];
};

export default observer<Props>(function EditGeneral({ editedSite, policyOptions }) {
    const {
        viewStore: { callingCode, country },
        campaignStore,
        groupStore,
        blueprintStore
    } = useStore<RootStore>('rootStore');
    const [autoDispatchEnabled, setAutoDispatchEnabled] = useState(!!editedSite.policy);

    const toggleAutoDispatchEnabled = () =>
        setAutoDispatchEnabled((prevAutoDispatchEnabled) => !prevAutoDispatchEnabled);

    const campaignOptions = sortBy(
        campaignStore.campaigns.map((c) => ({ label: c.name, value: c.id })),
        ['label']
    );
    const groupOptions = sortBy(
        groupStore.groups.map((g) => ({ label: g.name, value: g.id })),
        ['label']
    );

    const blueprintOptions = sortBy(
        blueprintStore.blueprints.map((b: Blueprint) => ({ label: b.name, value: b.id })),
        ['label']
    );

    useEffect(() => {
        if (!autoDispatchEnabled) {
            editedSite.policy = null;
        } else {
            if (policyOptions?.length && !editedSite.policy)
                editedSite.policy = { id: policyOptions[0]!.value } as Policy;
        }
    }, [autoDispatchEnabled]);

    return (
        <>
            <FilterRow>
                <label>
                    Name
                    <Input
                        data-testid={qaLabelTestSites.addSiteModal.nameInput}
                        value={editedSite.contact?.name ? editedSite.contact.name : ''}
                        onChange={action((e) => (editedSite.contact!.name = e.target.value))}
                    />
                </label>
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.phoneRow}>
                <InputLabel optional label="Phone Number">
                    <div style={{ marginTop: 5 }} />
                    <TelInput
                        value={editedSite.contact?.phone || ''}
                        default={callingCode}
                        defaultCountry={country}
                        onChange={action((number: string) => {
                            editedSite.contact!.phone = number.length ? number : null;
                        })}
                    />
                </InputLabel>
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.groupRow}>
                <label>Group</label>
                <Select
                    value={
                        editedSite.group
                            ? groupOptions.find((g) => g.value === editedSite.group)
                            : null
                    }
                    onChange={action((selected: NumberOption) => {
                        editedSite.group = selected?.value;
                    })}
                    options={groupOptions}
                    placeholder="Select a Group..."
                />
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.autoDispatcherRow}>
                <AutodispatcherHeader
                    autoDispatchEnabled={autoDispatchEnabled}
                    hasPolicies={!!policyOptions.length}
                    onChange={toggleAutoDispatchEnabled}
                />
                <Select
                    isDisabled={!autoDispatchEnabled}
                    value={
                        editedSite.policy
                            ? policyOptions.find((p) => p.value === editedSite.policy!.id)
                            : null
                    }
                    onChange={(selected: NumberOption) => {
                        editedSite.policy = selected ? ({ id: selected?.value } as Policy) : null;
                    }}
                    options={policyOptions}
                    placeholder={
                        autoDispatchEnabled ? 'Select an Auto Dispatcher...' : 'Manual dispatch'
                    }
                />
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.campaignRow}>
                <label>Campaign</label>
                <Select
                    value={
                        editedSite.campaign
                            ? campaignOptions.find((c) => c.value === editedSite.campaign!.id)
                            : null
                    }
                    onChange={action((selected: NumberOption) => {
                        editedSite.campaign = selected ? { id: selected.value } : null;
                    })}
                    options={campaignOptions}
                    placeholder="Select a Campaign..."
                    isClearable
                />
            </FilterRow>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.jobTemplateRow}>
                <label>Job Template</label>
                <Select
                    value={
                        editedSite.blueprint?.id
                            ? blueprintOptions.find((it) => it.value === editedSite.blueprint!.id)
                            : null
                    }
                    onChange={action((selected: NumberOption) => {
                        editedSite.blueprint = selected
                            ? ({ id: selected.value } as Blueprint)
                            : undefined;
                    })}
                    options={blueprintOptions}
                    placeholder="Select a Job Template..."
                    isClearable
                />
            </FilterRow>
        </>
    );
});
